/**
 * TrendBuy Shoppable Reels - GRID APP CONTROLLER (v3)
 *
 * New in this version:
 *  - Infinite scroll now has a guaranteed fallback: a visible "Load more
 *    reels" button calls the exact same fetch function as the
 *    IntersectionObserver, so pagination can't silently stop working if
 *    a theme ancestor clips the scroll sentinel (a common Shopify issue).
 *  - The quick-add drawer now fetches full product data and renders a
 *    real variant picker (size/color buttons), matching the reference
 *    reels-feed design, instead of only ever adding the first variant.
 *  - Everything from v2 (preload window, grid hover/inview preview,
 *    history-based back button, session randomization) is unchanged.
 */

(function () {
  'use strict';

  const STORAGE_KEY = 'reels_liked';
  const SESSION_KEY = 'reelsAppSession';
  const PRELOAD_RADIUS = 1;

  const state = {
    isMuted: true,
    likedProducts: new Set(),
    currentShareUrl: '',
    currentShareTitle: '',
    activeGridPreview: null,
    activeModalIndex: -1,
    isModalOpen: false,
    reducedData: false,
    currentDrawerProduct: null,
    currentDrawerVariantId: null
  };

  let elements = {};

  function cacheElements() {
    elements = {
      root: document.getElementById('reels-app-container-main'),
      gridContainer: document.getElementById('reels-grid-feed'),
      modalOverlay: document.getElementById('reels-modal-overlay'),
      modalFeed: document.getElementById('reels-modal-feed'),
      closeModalBtn: document.querySelector('.close-modal-btn'),
      drawer: document.getElementById('reel-drawer-app'),
      drawerVariants: document.getElementById('drawer-variants-app'),
      shareModal: document.getElementById('reel-share-modal-app'),
      toast: document.getElementById('reel-toast-app'),
      loadMoreWrap: document.getElementById('reels-load-more'),
      loadMoreBtn: document.getElementById('reels-load-more-btn')
    };
  }

  function isDataSaverMode() {
    const conn = navigator.connection || navigator.webkitConnection || navigator.mozConnection;
    if (conn && (conn.saveData || /2g/.test(conn.effectiveType || ''))) return true;
    if (window.matchMedia && window.matchMedia('(prefers-reduced-data: reduce)').matches) return true;
    return false;
  }

  // ==========================================================================
  // 1. INITIALIZATION
  // ==========================================================================

  function initApp() {
    cacheElements();
    if (!elements.gridContainer) return;

    state.reducedData = isDataSaverMode();

    elements.gridContainer.classList.add('is-ready');
    const loader = document.getElementById('reels-loader');
    if (loader) loader.style.display = 'none';

    restoreOrShuffleGrid();
    reindexGrid();

    bindEvents();
    setupGridPreviewObserver();
    setupModalVideoObserver();
    setupHistory();
    loadLikedProducts();

    const totalPages = parseInt(elements.gridContainer.getAttribute('data-total-pages'), 10) || 1;
    let availablePages = [];
    for (let i = 2; i <= totalPages; i++) availablePages.push(i);
    availablePages.sort(() => Math.random() - 0.5);

    setupInfiniteScroll(availablePages);
    maybeOpenFromDeepLink();

    window.addEventListener('beforeunload', persistSessionState);
    window.addEventListener('pagehide', persistSessionState);
  }

  function reindexGrid() {
    const gridItems = Array.from(elements.gridContainer.querySelectorAll('.grid-item'));
    const modalItems = Array.from(elements.modalFeed.children);
    gridItems.forEach((item, idx) => {
      item.dataset.index = idx;
      if (modalItems[idx]) modalItems[idx].dataset.index = idx;
    });
  }

  // ==========================================================================
  // 2. SESSION ORDER
  // ==========================================================================

  function getNavigationType() {
    try {
      const nav = performance.getEntriesByType('navigation')[0];
      if (nav) return nav.type;
    } catch (e) { /* noop */ }
    return null;
  }

  function restoreOrShuffleGrid() {
    const randomizeEnabled = elements.root.getAttribute('data-randomize') === 'true';
    const navType = getNavigationType();
    let saved = null;

    try { saved = JSON.parse(sessionStorage.getItem(SESSION_KEY) || 'null'); } catch (e) { saved = null; }

    const isReturning = navType === 'back_forward' || (saved && saved.order && saved.order.length);

    if (isReturning && saved && saved.order && saved.order.length) {
      applyOrder(saved.order);
      if (typeof saved.scrollTop === 'number') {
        requestAnimationFrame(() => { window.scrollTo(0, saved.scrollTop); });
      }
      return;
    }

    if (randomizeEnabled) shuffleGridDom();
  }

  function shuffleGridDom() {
    const gridItems = Array.from(elements.gridContainer.children);
    const modalItems = Array.from(elements.modalFeed.children);
    const pairs = gridItems.map((g, i) => ({ grid: g, modal: modalItems[i] || null }));

    for (let i = pairs.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [pairs[i], pairs[j]] = [pairs[j], pairs[i]];
    }

    pairs.forEach((pair) => {
      elements.gridContainer.appendChild(pair.grid);
      if (pair.modal) elements.modalFeed.appendChild(pair.modal);
    });
  }

  function applyOrder(productIdOrder) {
    const gridMap = new Map();
    Array.from(elements.gridContainer.children).forEach((el) => gridMap.set(el.dataset.productId, el));
    const modalMap = new Map();
    Array.from(elements.modalFeed.children).forEach((el) => modalMap.set(el.dataset.productId, el));

    productIdOrder.forEach((id) => {
      const g = gridMap.get(id);
      const m = modalMap.get(id);
      if (g) elements.gridContainer.appendChild(g);
      if (m) elements.modalFeed.appendChild(m);
    });
  }

  function persistSessionState() {
    const order = Array.from(elements.gridContainer.children).map((el) => el.dataset.productId);
    const payload = { order, scrollTop: window.scrollY || window.pageYOffset || 0 };
    try { sessionStorage.setItem(SESSION_KEY, JSON.stringify(payload)); } catch (e) { /* storage full/unavailable */ }
  }

  // ==========================================================================
  // 3. GRID PREVIEW
  // ==========================================================================

  function setupGridPreviewObserver() {
    const previewEnabled = elements.root.getAttribute('data-enable-preview') === 'true' && !state.reducedData;
    if (!previewEnabled) return;

    const supportsHover = window.matchMedia('(hover: hover)').matches;

    if (supportsHover) {
      elements.gridContainer.addEventListener('mouseenter', (e) => {
        const item = e.target.closest && e.target.closest('.grid-item');
        if (item) startGridPreview(item);
      }, true);

      elements.gridContainer.addEventListener('mouseleave', (e) => {
        const item = e.target.closest && e.target.closest('.grid-item');
        if (item) stopGridPreview(item);
      }, true);
    } else {
      const observer = new IntersectionObserver((entries) => {
        let best = null;
        entries.forEach((entry) => {
          if (entry.isIntersecting && (!best || entry.intersectionRatio > best.intersectionRatio)) {
            best = entry;
          } else if (!entry.isIntersecting) {
            stopGridPreview(entry.target);
          }
        });
        if (best) startGridPreview(best.target);
      }, { threshold: [0, 0.6, 1] });

      document.querySelectorAll('.grid-item').forEach((item) => observer.observe(item));
      document.addEventListener('reels-app:newItems', () => {
        document.querySelectorAll('.grid-item:not(.preview-observed)').forEach((item) => {
          item.classList.add('preview-observed');
          observer.observe(item);
        });
      });
    }
  }

  function startGridPreview(item) {
    if (!item || !item.dataset.videoUrl) return;
    if (state.activeGridPreview && state.activeGridPreview.el === item) return;
    if (state.activeGridPreview) stopGridPreview(state.activeGridPreview.el);

    let video = item.querySelector('.grid-item-video');
    if (!video) {
      video = document.createElement('video');
      video.className = 'grid-item-video';
      video.src = item.dataset.videoUrl;
      video.muted = true;
      video.loop = true;
      video.playsInline = true;
      video.preload = 'metadata';
      item.insertBefore(video, item.querySelector('.grid-item-overlay'));
    }
    video.play().catch(() => {});
    requestAnimationFrame(() => video.classList.add('is-active'));

    state.activeGridPreview = { el: item, video };
  }

  function stopGridPreview(item) {
    if (!item) return;
    const video = item.querySelector('.grid-item-video');
    if (video) {
      video.classList.remove('is-active');
      video.pause();
      setTimeout(() => { if (!video.classList.contains('is-active')) video.remove(); }, 250);
    }
    if (state.activeGridPreview && state.activeGridPreview.el === item) state.activeGridPreview = null;
  }

  // ==========================================================================
  // 4. FULL-SCREEN MODAL VIDEO - PRELOAD WINDOW
  // ==========================================================================

  function setupModalVideoObserver() {
    const options = { root: elements.modalFeed, threshold: 0.6 };

    const videoObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          const idx = parseInt(entry.target.dataset.index, 10);
          state.activeModalIndex = idx;
          ensurePreloadWindow(idx);
          playReel(entry.target);
          updateHistoryForActiveReel(entry.target);
        } else {
          pauseReel(entry.target);
        }
      });
    }, options);

    document.querySelectorAll('.modal-reel').forEach((reel) => videoObserver.observe(reel));

    document.addEventListener('reels-app:newItems', () => {
      document.querySelectorAll('.modal-reel:not(.observed)').forEach((reel) => {
        reel.classList.add('observed');
        videoObserver.observe(reel);
      });
    });
  }

  function getReelByIndex(idx) {
    return elements.modalFeed.querySelector(`.modal-reel[data-index="${idx}"]`);
  }

  function ensurePreloadWindow(centerIndex) {
    const allReels = Array.from(elements.modalFeed.children);
    const keepRange = { min: centerIndex - PRELOAD_RADIUS, max: centerIndex + PRELOAD_RADIUS };

    allReels.forEach((reel) => {
      const idx = parseInt(reel.dataset.index, 10);
      const inWindow = idx >= keepRange.min && idx <= keepRange.max;
      if (inWindow) {
        createVideoIfNeeded(reel, idx === centerIndex ? 'auto' : 'metadata');
      } else {
        teardownVideo(reel);
      }
    });
  }

  function createVideoIfNeeded(reel, preloadMode) {
    const placeholder = reel.querySelector('.video-placeholder');
    let video = reel.querySelector('video.modal-video');
    if (video) return video;

    video = document.createElement('video');
    video.className = 'modal-video reel-video';
    video.src = placeholder.dataset.videoUrl;
    video.loop = true;
    video.playsInline = true;
    video.muted = state.isMuted;
    video.preload = preloadMode;

    video.addEventListener('loadeddata', () => {
      video.classList.add('is-ready');
      reel.classList.remove('is-buffering');
    });
    video.addEventListener('waiting', () => reel.classList.add('is-buffering'));
    video.addEventListener('playing', () => { reel.classList.remove('is-buffering'); reel.classList.remove('is-paused'); });
    video.addEventListener('pause', () => reel.classList.add('is-paused'));
    video.addEventListener('play', () => reel.classList.remove('is-paused'));
    video.addEventListener('error', () => reel.classList.add('has-error'));

    placeholder.appendChild(video);
    return video;
  }

  function teardownVideo(reel) {
    const video = reel.querySelector('video.modal-video');
    if (video) {
      video.pause();
      video.removeAttribute('src');
      video.load();
      video.remove();
    }
    reel.classList.remove('is-buffering', 'is-paused', 'has-error');
  }

  function playReel(reel) {
    const video = reel.querySelector('video.modal-video') || createVideoIfNeeded(reel, 'auto');
    video.play().then(() => {
      reel.classList.remove('is-paused');
    }).catch(() => {
      reel.classList.add('is-paused');
    });
    const muteBtn = reel.querySelector('.reel-control--mute');
    if (muteBtn) muteBtn.classList.toggle('muted', state.isMuted);
  }

  function pauseReel(reel) {
    const video = reel.querySelector('video.modal-video');
    if (video) video.pause();
  }

  function retryReel(reel) {
    reel.classList.remove('has-error');
    teardownVideo(reel);
    createVideoIfNeeded(reel, 'auto');
    playReel(reel);
  }

  // ==========================================================================
  // 5. HISTORY / BACK-BUTTON SUPPORT
  // ==========================================================================

  function setupHistory() {
    if (!history.state || !history.state.reelsGrid) {
      history.replaceState({ reelsGrid: true }, '', window.location.pathname + window.location.search.replace(/[?&]reel=[^&]*/, ''));
    }

    window.addEventListener('popstate', (e) => {
      if (e.state && e.state.reelsModal) {
        openModalAtIndex(e.state.reelIndex, false);
      } else {
        closeModalInternal(false);
      }
    });
  }

  function updateHistoryForActiveReel(reel) {
    if (!state.isModalOpen) return;
    const productId = reel.dataset.productId;
    const idx = reel.dataset.index;
    const url = window.location.pathname + '?reel=' + encodeURIComponent(productId);
    const current = history.state || {};
    if (current.reelIndex === idx && current.reelsModal) return;
    history.replaceState({ reelsModal: true, reelIndex: idx, productId }, '', url);
  }

  function maybeOpenFromDeepLink() {
    const params = new URLSearchParams(window.location.search);
    const reelProductId = params.get('reel');
    if (!reelProductId) return;
    const target = elements.gridContainer.querySelector(`.grid-item[data-product-id="${CSS.escape(reelProductId)}"]`);
    if (target) openModalFromGridItem(target, true);
  }

  // ==========================================================================
  // 6. INFINITE SCROLL + GUARANTEED "LOAD MORE" FALLBACK
  // ==========================================================================

  function setupInfiniteScroll(availablePages) {
    let isFetching = false;
    const currentPath = window.location.pathname.includes('/editor') ? '/' : window.location.pathname;
    const ajaxView = elements.gridContainer.getAttribute('data-ajax-view') || 'reels-grid-json';

    if (availablePages.length === 0) {
      if (elements.loadMoreWrap) elements.loadMoreWrap.classList.remove('is-visible');
      return;
    }

    async function fetchNextPage(trigger) {
      if (isFetching || availablePages.length === 0) return;
      isFetching = true;
      if (elements.loadMoreBtn) {
        elements.loadMoreBtn.classList.add('is-loading');
        elements.loadMoreBtn.disabled = true;
      }

      const nextPage = availablePages.pop();

      try {
        // Uses Shopify's alternate-template mechanism (?view=...), NOT the
        // Section Rendering API - see templates/page.reels-grid-json.liquid
        // for why: it's the only one of the two that reliably honors the
        // "page" query param on a custom Page route.
        const response = await fetch(`${currentPath}?view=${ajaxView}&page=${nextPage}`);
        if (!response.ok) throw new Error('Reels page fetch failed: ' + response.status);
        const html = await response.text();
        const newDoc = new DOMParser().parseFromString(html, 'text/html');

        // The alt template returns a bare fragment (no wrapping containers,
        // thanks to {% layout none %}), so select by class directly.
        const newGrid = Array.from(newDoc.querySelectorAll('.grid-item'));
        const newModal = Array.from(newDoc.querySelectorAll('.modal-reel'));

        let pairs = newGrid.map((g, i) => ({ grid: g, modal: newModal[i] }));
        pairs.sort(() => Math.random() - 0.5);

        const currentCount = elements.gridContainer.children.length;

        pairs.forEach((pair, idx) => {
          if (pair.grid && pair.modal) {
            pair.grid.dataset.index = currentCount + idx;
            pair.modal.dataset.index = currentCount + idx;
            elements.gridContainer.appendChild(pair.grid);
            elements.modalFeed.appendChild(pair.modal);
          }
        });

        reAttachScrollSentinel();
        document.dispatchEvent(new CustomEvent('reels-app:newItems'));
        syncLikedState();
        queueScrollCheck();
        if (elements.loadMoreWrap) elements.loadMoreWrap.classList.remove('is-visible');

      } catch (e) {
        console.error(e);
        // Put the page back so a retry can pick it up again, and only NOW
        // reveal the button - it's a retry affordance for a real failure,
        // not the primary way to load more (that should feel continuous).
        availablePages.push(nextPage);
        if (elements.loadMoreWrap) elements.loadMoreWrap.classList.add('is-visible');
        showToast("Couldn't load more reels — tap Load more to retry.");
      } finally {
        isFetching = false;
        if (elements.loadMoreBtn) {
          elements.loadMoreBtn.classList.remove('is-loading');
          elements.loadMoreBtn.disabled = false;
        }
        if (availablePages.length === 0 && elements.loadMoreWrap) {
          elements.loadMoreWrap.classList.remove('is-visible');
        }
      }
    }

    // Auto-trigger via IntersectionObserver on a dedicated sentinel.
    const scrollObserver = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting) fetchNextPage('scroll');
    }, { rootMargin: '0px 0px 800px 0px' });

    function reAttachScrollSentinel() {
      const lastGrid = elements.gridContainer.lastElementChild;
      if (lastGrid) {
        scrollObserver.disconnect();
        scrollObserver.observe(lastGrid);
      }
    }
    reAttachScrollSentinel();

    // FALLBACK: IntersectionObserver's default root is the viewport, but
    // it still clips intersection against any ANCESTOR with overflow set
    // (a very common thing in Shopify section wrappers) - if that happens
    // the sentinel can report 0% visible forever even though it's clearly
    // on screen, and auto-load silently never fires. getBoundingClientRect
    // is not affected by that, so we double-check scroll position directly
    // as a second, independent trigger.
    let scrollCheckQueued = false;
    function checkScrollFallback() {
      scrollCheckQueued = false;
      if (isFetching || availablePages.length === 0) return;
      const lastGrid = elements.gridContainer.lastElementChild;
      if (!lastGrid) return;
      const rect = lastGrid.getBoundingClientRect();
      if (rect.top < window.innerHeight + 800) {
        fetchNextPage('scroll-fallback');
      }
    }
    function queueScrollCheck() {
      if (scrollCheckQueued) return;
      scrollCheckQueued = true;
      requestAnimationFrame(checkScrollFallback);
    }
    window.addEventListener('scroll', queueScrollCheck, { passive: true });
    window.addEventListener('resize', queueScrollCheck);
    // Also run once right away: if the first page of the grid doesn't
    // fill the viewport, there's nothing to scroll and this is the only
    // thing that will kick off loading page 2.
    queueScrollCheck();

    // Guaranteed manual fallback - always available regardless of whether
    // either auto-trigger fires.
    if (elements.loadMoreBtn) {
      elements.loadMoreBtn.addEventListener('click', () => fetchNextPage('manual'));
    }
  }

  // ==========================================================================
  // 7. UI EVENTS & ACTIONS
  // ==========================================================================

  function bindEvents() {
    elements.gridContainer.addEventListener('click', (e) => {
      const item = e.target.closest('.grid-item');
      if (!item) return;
      openModalFromGridItem(item, true);
    });

    elements.closeModalBtn.addEventListener('click', () => {
      if (history.state && history.state.reelsModal) {
        history.back();
      } else {
        closeModalInternal(true);
      }
    });

    elements.modalFeed.addEventListener('click', (e) => {
      const reel = e.target.closest('.modal-reel');
      if (!reel) return;

      if (e.target.closest('.reel-retry-btn')) {
        retryReel(reel);
        return;
      }

      if (e.target.closest('.reel-tap-overlay')) {
        const video = reel.querySelector('video.modal-video');
        if (video) { video.paused ? video.play().catch(() => {}) : video.pause(); }
        return;
      }

      if (e.target.closest('.reel-control--mute')) {
        state.isMuted = !state.isMuted;
        document.querySelectorAll('.modal-video').forEach((v) => { v.muted = state.isMuted; });
        document.querySelectorAll('.reel-control--mute').forEach((btn) => btn.classList.toggle('muted', state.isMuted));
        return;
      }

      if (e.target.closest('.reel-action--like')) {
        toggleLike(e.target.closest('.reel-action--like'));
        return;
      }

      if (e.target.closest('.reel-action--share')) {
        const btn = e.target.closest('.reel-action--share');
        openShareModal(btn.dataset.productUrl, btn.dataset.productTitle);
        return;
      }

      if (e.target.closest('.reel-action--cart') || e.target.closest('.reel-product-card__buy')) {
        e.preventDefault();
        openDrawer(reel);
        return;
      }
    });

    elements.drawer.addEventListener('click', (e) => {
      if (e.target.closest('.reel-drawer__close') || e.target.classList.contains('reel-drawer__overlay')) {
        elements.drawer.setAttribute('aria-hidden', 'true');
        return;
      }
      if (e.target.closest('[data-action="increase"]')) {
        const input = document.getElementById('drawer-quantity-app');
        input.value = Math.max(1, parseInt(input.value || '1', 10) + 1);
        return;
      }
      if (e.target.closest('[data-action="decrease"]')) {
        const input = document.getElementById('drawer-quantity-app');
        input.value = Math.max(1, parseInt(input.value || '1', 10) - 1);
        return;
      }
      const variantBtn = e.target.closest('.reel-drawer__variant-btn');
      if (variantBtn && !variantBtn.disabled) {
        selectDrawerVariant(variantBtn.dataset.variantId);
        return;
      }
      if (e.target.closest('#drawer-add-btn-app')) {
        addToCart(state.currentDrawerVariantId, document.getElementById('drawer-quantity-app').value);
      }
    });

    elements.shareModal.addEventListener('click', (e) => {
      if (e.target.closest('.reel-share-modal__close') || e.target.classList.contains('reel-share-modal__overlay')) {
        elements.shareModal.setAttribute('aria-hidden', 'true');
        return;
      }
      if (e.target.closest('.reel-share-option')) {
        handleShare(e.target.closest('.reel-share-option').dataset.share);
      }
    });

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && state.isModalOpen) {
        if (history.state && history.state.reelsModal) history.back();
        else closeModalInternal(true);
      }
    });
  }

  function openModalFromGridItem(item, pushHistory) {
    const index = item.dataset.index;
    const targetModalReel = elements.modalFeed.querySelector(`.modal-reel[data-index="${index}"]`);
    if (!targetModalReel) return;
    openModalAtIndex(index, pushHistory, targetModalReel);
  }

  function openModalAtIndex(index, pushHistory, knownReel) {
    const targetModalReel = knownReel || getReelByIndex(index);
    if (!targetModalReel) return;

    elements.modalOverlay.classList.add('is-open');
    document.body.style.overflow = 'hidden';
    state.isModalOpen = true;
    targetModalReel.scrollIntoView({ behavior: 'auto' });

    if (pushHistory) {
      const productId = targetModalReel.dataset.productId;
      const url = window.location.pathname + '?reel=' + encodeURIComponent(productId);
      history.pushState({ reelsModal: true, reelIndex: index, productId }, '', url);
    }

    ensurePreloadWindow(parseInt(index, 10));
    playReel(targetModalReel);
  }

  function closeModalInternal(replaceUrl) {
    elements.modalOverlay.classList.remove('is-open');
    document.body.style.overflow = '';
    state.isModalOpen = false;
    document.querySelectorAll('.modal-video').forEach((v) => v.pause());

    if (replaceUrl) {
      history.replaceState({ reelsGrid: true }, '', window.location.pathname);
    }
  }

  // ==========================================================================
  // 8. DRAWER + VARIANT PICKER
  // ==========================================================================

  function openDrawer(reel) {
    const productUrl = reel.dataset.productUrl;
    const fallbackVariantId = reel.dataset.variantId;

    fetch(`${productUrl}.js`)
      .then((r) => {
        if (!r.ok) throw new Error('Product fetch failed: ' + r.status);
        return r.json();
      })
      .then((product) => {
        state.currentDrawerProduct = product;
        const initialVariant = product.variants.find((v) => String(v.id) === String(fallbackVariantId)) || product.variants[0];
        state.currentDrawerVariantId = initialVariant ? initialVariant.id : null;

        elements.drawer.querySelector('.reel-drawer__image').src = product.featured_image || (product.images && product.images[0]) || '';
        elements.drawer.querySelector('.reel-drawer__title').textContent = product.title;
        renderDrawerVariants(product, state.currentDrawerVariantId);
        updateDrawerForVariant(initialVariant);

        elements.drawer.setAttribute('aria-hidden', 'false');
      })
      .catch((err) => {
        console.error(err);
        showToast("Couldn't load product details — try again.");
      });
  }

  function renderDrawerVariants(product, selectedVariantId) {
    elements.drawerVariants.innerHTML = '';
    // Only show a picker if the product actually has more than one variant.
    if (!product.variants || product.variants.length <= 1) return;

    product.variants.forEach((variant) => {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'reel-drawer__variant-btn';
      btn.dataset.variantId = variant.id;
      btn.textContent = variant.title === 'Default Title' ? variant.option1 : variant.title;
      if (!variant.available) btn.disabled = true;
      if (String(variant.id) === String(selectedVariantId)) btn.classList.add('selected');
      elements.drawerVariants.appendChild(btn);
    });
  }

  function selectDrawerVariant(variantId) {
    if (!state.currentDrawerProduct) return;
    const variant = state.currentDrawerProduct.variants.find((v) => String(v.id) === String(variantId));
    if (!variant) return;

    state.currentDrawerVariantId = variant.id;
    elements.drawerVariants.querySelectorAll('.reel-drawer__variant-btn').forEach((btn) => {
      btn.classList.toggle('selected', String(btn.dataset.variantId) === String(variantId));
    });
    updateDrawerForVariant(variant);
  }

  function updateDrawerForVariant(variant) {
    const addBtn = document.getElementById('drawer-add-btn-app');
    if (!variant) {
      addBtn.disabled = true;
      return;
    }
    elements.drawer.querySelector('.reel-drawer__price').textContent = `₹${(variant.price / 100).toFixed(2)}`;
    if (variant.featured_image && variant.featured_image.src) {
      elements.drawer.querySelector('.reel-drawer__image').src = variant.featured_image.src;
    }
    addBtn.dataset.variantId = variant.id;
    addBtn.disabled = !variant.available;
    addBtn.querySelector('span').textContent = variant.available ? 'Add to Cart' : 'Sold Out';
  }

  function addToCart(variantId, qty) {
    if (!variantId) {
      showToast('Please select an option first.');
      return;
    }
    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ items: [{ id: parseInt(variantId, 10), quantity: parseInt(qty, 10) }] })
    })
      .then((r) => {
        if (!r.ok) throw new Error('Add to cart failed: ' + r.status);
        elements.drawer.setAttribute('aria-hidden', 'true');
        showToast('Added to cart! 🛒', 'success');
      })
      .catch((err) => {
        console.error(err);
        showToast("Couldn't add to cart — try again.");
      });
  }

  // ==========================================================================
  // 9. HELPER FUNCTIONS
  // ==========================================================================

  function toggleLike(btn) {
    const id = btn.dataset.productId;
    if (state.likedProducts.has(id)) {
      state.likedProducts.delete(id);
      btn.classList.remove('liked');
    } else {
      state.likedProducts.add(id);
      btn.classList.add('liked');
      showToast('Added to wishlist ❤️', 'success');
    }
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify([...state.likedProducts])); } catch (e) { /* storage unavailable */ }
  }

  function loadLikedProducts() {
    let saved = null;
    try { saved = JSON.parse(localStorage.getItem(STORAGE_KEY) || 'null'); } catch (e) { saved = null; }
    if (saved) {
      saved.forEach((id) => state.likedProducts.add(id));
      syncLikedState();
    }
  }

  function syncLikedState() {
    document.querySelectorAll('.reel-action--like').forEach((btn) => {
      btn.classList.toggle('liked', state.likedProducts.has(btn.dataset.productId));
    });
  }

  function openShareModal(url, title) {
    state.currentShareUrl = url;
    state.currentShareTitle = title;
    elements.shareModal.setAttribute('aria-hidden', 'false');
  }

  function handleShare(platform) {
    if (platform === 'whatsapp') {
      const text = encodeURIComponent(`${state.currentShareTitle} ${state.currentShareUrl}`);
      window.open(`https://wa.me/?text=${text}`, '_blank', 'noopener');
    }
    if (platform === 'copy') {
      navigator.clipboard.writeText(state.currentShareUrl)
        .then(() => showToast('Link Copied!'))
        .catch(() => showToast('Could not copy link'));
    }
  }

  function showToast(msg) {
    elements.toast.textContent = msg;
    elements.toast.classList.add('visible');
    clearTimeout(showToast._t);
    showToast._t = setTimeout(() => elements.toast.classList.remove('visible'), 3000);
  }

  document.addEventListener('DOMContentLoaded', initApp);

})();
